/**
 * Whether the player has opened the Market Outlook for the year being decided: the rail's quiet dot and the round guide clear once they have (per browser).
 * Everything asks about the same round: `outlookRoundOf(asOfRound)`, the round after the last released one, which is the year the outlook is "as of the start of".
 * Opening it (page or drawer) writes the mark and tells every reader, so the dot clears at once rather than at the next render.
 */
import { useSyncExternalStore } from "react";

const key = (cohortId: string, round: number): string => `bsim.outlook.seen.${cohortId}.${round}`;

/** The year the outlook speaks for, from the last released round it was drawn at (0 before any). */
export const outlookRoundOf = (asOfRound: number): number => asOfRound + 1;

const listeners = new Set<() => void>();

export function outlookSeen(cohortId: string, round: number): boolean {
  try { return localStorage.getItem(key(cohortId, round)) === "1"; } catch { return false; }
}

export function markOutlookSeen(cohortId: string, round: number): void {
  try { localStorage.setItem(key(cohortId, round), "1"); } catch { /* storage unavailable: the dot simply stays */ }
  for (const l of [...listeners]) l();
}

const subscribe = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => { listeners.delete(l); };
};

/** Live: re-renders the caller when the outlook is opened (here or in another tab). */
export function useOutlookSeen(cohortId: string, round: number | null): boolean {
  return useSyncExternalStore(
    (l) => {
      const off = subscribe(l);
      window.addEventListener("storage", l);
      return () => { off(); window.removeEventListener("storage", l); };
    },
    () => round === null || outlookSeen(cohortId, round),
    () => true,
  );
}
